/**
 * Security utilities for JWT authentication
 * Provides additional security layers for token management
 */

/**
 * Generate a browser fingerprint for device binding
 * This helps prevent token theft by binding tokens to specific devices
 */
export const getBrowserFingerprint = async (): Promise<string> => {
  const components = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset(),
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    navigator.hardwareConcurrency || 'unknown',
  ];

  const fingerprint = components.join('|');
  
  // Create a hash of the fingerprint
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
};

/**
 * Validate JWT token structure
 * Ensures the token has the correct format before attempting to decode
 */
export const validateTokenStructure = (token: string): boolean => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // JWT should have 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Each part should be base64 encoded
  try {
    parts.forEach(part => {
      if (!part) throw new Error('Empty part');
      // Try to decode base64
      atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Check if a token is expired based on its exp claim
 */
export const isTokenExpired = (exp: number): boolean => {
  const currentTime = Math.floor(Date.now() / 1000);
  return exp < currentTime;
};

/**
 * Calculate time until token expiration in seconds
 */
export const getTimeUntilExpiration = (exp: number): number => {
  const currentTime = Math.floor(Date.now() / 1000);
  return Math.max(0, exp - currentTime);
};

/**
 * Check if token will expire soon (within threshold seconds)
 */
export const isTokenExpiringSoon = (exp: number, thresholdSeconds: number = 180): boolean => {
  const timeUntilExpiration = getTimeUntilExpiration(exp);
  return timeUntilExpiration > 0 && timeUntilExpiration < thresholdSeconds;
};

/**
 * Secure random string generator for nonces, etc.
 */
export const generateSecureRandomString = (length: number = 32): string => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'jade_access_token',
  REFRESH_TOKEN: 'jade_refresh_token',
  REMEMBER_ME: 'jade_remember_me',
  FINGERPRINT: 'jade_fingerprint',
} as const;

/**
 * Token metadata interface for secure storage
 */
export interface TokenMetadata {
  token: string;
  createdAt: number;
  expiresAt: number;
  fingerprint?: string;
}

/**
 * Store token with metadata
 */
export const storeTokenWithMetadata = (
  key: string,
  token: string,
  expiresIn: number,
  storage: Storage,
  fingerprint?: string
): void => {
  const metadata: TokenMetadata = {
    token,
    createdAt: Date.now(),
    expiresAt: Date.now() + expiresIn * 1000,
    fingerprint,
  };
  
  storage.setItem(key, JSON.stringify(metadata));
};

/**
 * Retrieve token with metadata validation
 */
export const getTokenWithMetadata = (
  key: string,
  storage: Storage,
  validateFingerprint?: string
): string | null => {
  try {
    const stored = storage.getItem(key);
    if (!stored) return null;

    const metadata: TokenMetadata = JSON.parse(stored);
    
    // Check if token is expired
    if (metadata.expiresAt && Date.now() > metadata.expiresAt) {
      storage.removeItem(key);
      return null;
    }

    // Validate fingerprint if provided
    if (validateFingerprint && metadata.fingerprint && metadata.fingerprint !== validateFingerprint) {
      // Don't log full error details to avoid exposing fingerprints
      storage.removeItem(key);
      return null;
    }

    return metadata.token;
  } catch (error) {
    // Silent cleanup - don't expose details
    storage.removeItem(key);
    return null;
  }
};

/**
 * Clean up expired tokens from storage
 */
export const cleanupExpiredTokens = (): void => {
  const storages = [localStorage, sessionStorage];
  
  storages.forEach(storage => {
    Object.values(STORAGE_KEYS).forEach(key => {
      try {
        const stored = storage.getItem(key);
        if (stored) {
          const metadata: TokenMetadata = JSON.parse(stored);
          if (metadata.expiresAt && Date.now() > metadata.expiresAt) {
            storage.removeItem(key);
          }
        }
      } catch (error) {
        // If parsing fails, remove the corrupted item
        storage.removeItem(key);
      }
    });
  });
};



